import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import CarrierAgreementModal from '@/components/CarrierAgreementModal';
import { Truck, User, Mail, Phone, MapPin, FileText, 
  CheckCircle, ArrowRight, Shield, DollarSign,
  Clock, Headphones, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/60d759fc-5f69-465c-83fb-ade59a36c7bf/images/5f275c2793af6e53f8d88d7d3274efa09efc6a6ea0813dc9205d29d9ed6d6fcd.png";

const DriverSignup = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [contractId, setContractId] = useState(null);
  const [agreementOpen, setAgreementOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    // Personal Info
    name: '',
    email: '',
    phone: '',
    
    // Business Info
    company_name: '',
    mc_number: '',
    dot_number: '',
    
    // Location
    home_base: '',
    operating_states: '',
    
    // Equipment
    truck_type: 'dry_van',
    truck_year: '',
    truck_make: '',
    
    // Agreement
    agree_terms: false,
    agree_fee: false,
    signature: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const validateStep1 = () => {
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all personal information');
      return false;
    }
    if (!formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.company_name || !formData.mc_number) {
      toast.error('Please fill in company name and MC number');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.agree_terms || !formData.agree_fee || !formData.agree_agency) {
      toast.error('Please agree to all terms');
      return false;
    }
    if (!formData.signature) {
      toast.error('Please type your name as signature');
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;
    // Carrier Dispatch Agreement must be signed BEFORE we create the driver record.
    setAgreementOpen(true);
  };

  const completeSignup = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/driver-signup`, formData);
      setContractId(response.data.contract_id);
      setSuccess(true);
      toast.success('Registration successful! Contract sent to your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-slate-800/50 border-slate-700 text-center">
          <CardContent className="p-8">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Welcome to Tempus Dispatch!</h2>
            <p className="text-gray-400 mb-6">
              Your registration is complete. We've sent your Dispatch Service Agreement to <strong className="text-white">{formData.email}</strong>.
            </p>
            <div className="bg-slate-700/50 rounded-lg p-4 mb-6 text-left">
              <h3 className="text-sm font-semibold text-gray-300 mb-2">What's Next?</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  Check your email for the contract
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  Our team will contact you within 24 hours
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  Get ready to receive load opportunities!
                </li>
              </ul>
            </div>
            <div className="flex gap-3">
              <Button 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={() => window.location.href = '/driver'}
              >
                Go to Driver Portal
              </Button>
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => window.location.href = '/'}
              >
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <a href="/">
            <img src={LOGO_URL} alt="Tempus Dispatch" className="h-20 mx-auto mb-4" />
          </a>
          <h1 className="text-3xl font-bold text-white mb-2">Hire Tempus as Your Dispatcher</h1>
          <p className="text-gray-400">Join 556,000+ owner operators in our network</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                step >= s ? 'bg-blue-600 text-white' : 'bg-slate-700 text-gray-500'
              }`}>
                {s}
              </div>
              {s < 3 && (
                <div className={`w-20 h-1 ${step > s ? 'bg-blue-600' : 'bg-slate-700'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  {step === 1 && <><User className="w-5 h-5 text-blue-500" /> Personal Information</>}
                  {step === 2 && <><Truck className="w-5 h-5 text-blue-500" /> Business & Equipment</>}
                  {step === 3 && <><FileText className="w-5 h-5 text-blue-500" /> Agreement & Signature</>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Step 1: Personal Info */}
                {step === 1 && (
                  <>
                    <div>
                      <Label className="text-gray-300">Full Name *</Label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Email Address *</Label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Phone Number *</Label>
                      <Input
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Home Base Location</Label>
                      <Input
                        name="home_base"
                        value={formData.home_base}
                        onChange={handleChange}
                        placeholder="Atlanta, GA"
                        className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                      />
                    </div>
                  </>
                )}

                {/* Step 2: Business Info */}
                {step === 2 && (
                  <>
                    <div>
                      <Label className="text-gray-300">Company / Business Name *</Label>
                      <Input
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        placeholder="Smith Trucking LLC"
                        className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-300">MC Number *</Label>
                        <Input
                          name="mc_number"
                          value={formData.mc_number}
                          onChange={handleChange}
                          placeholder="MC-123456"
                          className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">DOT Number</Label>
                        <Input
                          name="dot_number"
                          value={formData.dot_number}
                          onChange={handleChange}
                          placeholder="DOT-123456"
                          className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-300">Operating States</Label>
                      <Input
                        name="operating_states"
                        value={formData.operating_states}
                        onChange={handleChange}
                        placeholder="GA, FL, TX, CA (or 48 states)"
                        className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Truck Type</Label>
                      <select
                        name="truck_type"
                        value={formData.truck_type}
                        onChange={handleChange}
                        className="w-full mt-1 px-4 py-2 rounded-lg bg-slate-700/50 border border-slate-600 text-white"
                      >
                        <option value="sprinter_van">Sprinter Van</option>
                        <option value="cargo_van">Cargo Van</option>
                        <option value="box_truck">Box Truck</option>
                        <option value="hotshot">Hotshot</option>
                        <option value="dry_van">Dry Van</option>
                        <option value="reefer">Reefer</option>
                        <option value="flatbed">Flatbed</option>
                        <option value="step_deck">Step Deck</option>
                        <option value="conestoga">Conestoga</option>
                        <option value="lowboy">Lowboy</option>
                        <option value="rgn">RGN (Removable Gooseneck)</option>
                        <option value="double_drop">Double Drop</option>
                        <option value="curtain_side">Curtain Side</option>
                        <option value="tanker">Tanker</option>
                        <option value="hopper">Hopper</option>
                        <option value="dump_truck">Dump Truck</option>
                        <option value="power_only">Power Only</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-300">Truck Year</Label>
                        <Input
                          name="truck_year"
                          value={formData.truck_year}
                          onChange={handleChange}
                          placeholder="2022"
                          className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Truck Make</Label>
                        <Input
                          name="truck_make"
                          value={formData.truck_make}
                          onChange={handleChange}
                          placeholder="Freightliner"
                          className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Step 3: Agreement */}
                {step === 3 && (
                  <>
                    <div className="bg-slate-700/30 rounded-lg p-4 mb-4">
                      <h3 className="text-white font-semibold mb-3">Carrier Dispatch Agreement — Summary</h3>
                      <ul className="space-y-2 text-sm text-gray-300">
                        <li>• Tempus Dispatch becomes your <strong>authorized dispatch agent</strong> — we act on your behalf (you remain the principal on every load)</li>
                        <li>• Tempus is <strong>not a freight broker</strong> (49 USC § 13904) — all freight contracts are between you and the broker/shipper directly</li>
                        <li>• <strong className="text-orange-400">6% of gross monthly freight revenue</strong> — usage-based billing via Stripe, self-reported monthly</li>
                        <li>• <strong className="text-green-400">$0 monthly fee · $0 setup · no load limits</strong> — pay only when you earn</li>
                        <li>• Cancel anytime — no long-term contract</li>
                        <li>• Full Carrier Dispatch Agreement opens on next step for digital signature</li>
                      </ul>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="agree_terms"
                          checked={formData.agree_terms}
                          onCheckedChange={(checked) => handleCheckbox('agree_terms', checked)}
                        />
                        <label htmlFor="agree_terms" className="text-sm text-gray-300 cursor-pointer">
                          I have read and agree to the <a href="/terms" target="_blank" rel="noreferrer" className="text-blue-400 underline">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noreferrer" className="text-blue-400 underline">Privacy Policy</a> *
                        </label>
                      </div>
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="agree_fee"
                          checked={formData.agree_fee}
                          onCheckedChange={(checked) => handleCheckbox('agree_fee', checked)}
                        />
                        <label htmlFor="agree_fee" className="text-sm text-gray-300 cursor-pointer">
                          I understand Tempus charges <strong className="text-orange-400">6% of my gross monthly freight revenue</strong> (self-reported, billed via Stripe). No monthly fee, no load limits. *
                        </label>
                      </div>
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="agree_agency"
                          checked={formData.agree_agency || false}
                          onCheckedChange={(checked) => handleCheckbox('agree_agency', checked)}
                        />
                        <label htmlFor="agree_agency" className="text-sm text-gray-300 cursor-pointer">
                          I understand that if I enable Autopilot, Tempus acts solely as my <strong className="text-amber-400">software agent</strong> executing rules I configure. I remain the principal in every freight contract. *
                        </label>
                      </div>
                    </div>

                    <div className="mt-4">
                      <Label className="text-gray-300">Electronic Signature *</Label>
                      <p className="text-xs text-gray-500 mb-2">Type your full legal name as your electronic signature</p>
                      <Input
                        name="signature"
                        value={formData.signature}
                        onChange={handleChange}
                        placeholder="Type your full name"
                        className="mt-1 bg-slate-700/50 border-slate-600 text-white font-signature text-lg"
                        style={{ fontFamily: 'cursive' }}
                      />
                    </div>

                    <p className="text-xs text-gray-500 mt-4">
                      By signing above, you acknowledge that this electronic signature is legally binding and has the same legal effect as a handwritten signature.
                    </p>
                  </>
                )}

                {/* Navigation Buttons */}
                <div className="flex gap-3 pt-4">
                  {step > 1 && (
                    <Button variant="outline" onClick={prevStep} className="flex-1">
                      Back
                    </Button>
                  )}
                  {step < 3 ? (
                    <Button onClick={nextStep} className="flex-1 bg-blue-600 hover:bg-blue-700">
                      Continue <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleSubmit} 
                      disabled={loading}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      {loading ? 'Submitting...' : 'Complete Registration'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg">Why Tempus Dispatch?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Better Rates</h4>
                    <p className="text-sm text-gray-400">We negotiate the best rates from our 100K+ broker network</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Autopilot Books Loads For You</h4>
                    <p className="text-sm text-gray-400">Tempus is your authorized agent — we book, you drive</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">24/7 — Never Sleeps</h4>
                    <p className="text-sm text-gray-400">AI handles brokers around the clock, weekends included</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Headphones className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">30-Min Backup Coverage</h4>
                    <p className="text-sm text-gray-400">Cannot cover? Tempus auto-dispatches a backup, protects your reputation</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-600/20 to-orange-500/20 border-blue-500/30">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-white mb-1">6%</p>
                <p className="text-sm text-gray-300">of gross monthly freight revenue</p>
                <p className="text-xs text-gray-400 mt-2">No monthly fee · Pay only when you earn</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <CarrierAgreementModal
        open={agreementOpen}
        onOpenChange={setAgreementOpen}
        email={formData.email}
        defaultName={formData.name}
        defaultMcNumber={formData.mc_number}
        blocking
        onSigned={() => { setAgreementOpen(false); completeSignup(); }}
      />
    </div>
  );
};

export default DriverSignup;
